import Testaus from "./Testaus";
import Ohjeet from "./Ohjeet";
import Testauspalaute, { PalauteNappi } from "./Testauspalaute";
import { asetaAvattavat } from "./Linkit";
import KalustoQr from "./KalustoQr";
import { StrictMode, useCallback, useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import { api, ApiError, type Kayttaja, type Moduuli } from "./api";
import type { ModuuliTunnus } from "./shared/types";
import { haeBrandi, type Brandi } from "./brandi";
import { t, asetaKieli, asetaSelaimenKieli, asetaValittavat, valittavatKielet, kieli, virheTeksti, KIELEN_NIMI, type KaannosAvain } from "./kieli";
import { YlapalkinKieli } from "./KieliValinta";
import PinVaihto from "./PinVaihto";
import { ryhmanTunnus } from "./nayttotila";
import { EiOikeutta, type Esto } from "./EiOikeutta";
import Kapasiteetti from "./Kapasiteetti";
import Kohteet from "./Kohteet";
import Henkilot from "./Henkilot";
import { Rotaatio } from "./Rotaatio";
import { Hub } from "./Hub";
import { Suunnitelma } from "./Suunnitelma";
import LaatuAukot from "./LaatuAukot";
import PuutteetNakyma from "./Puutteet";
import Ainestodistukset from "./Ainestodistukset";
import { Tyopaketit } from "./Tyopaketit";
import { Varasto } from "./Varasto";
import { OmatTiedot } from "./OmatTiedot";
import { Tuntikirjaus } from "./Tuntikirjaus";
import Kayttajat from "./Kayttajat";
import Patevyydet from "./Patevyydet";
import Mitoitus from "./Mitoitus";
import Hitsausloki, { tyhjennaHitsauslokinIstunto } from "./Hitsausloki";
import Kalusto from "./Kalusto";
import Laiteasennus from "./Laiteasennus";
import IvAsennus from "./IvAsennus";
import Portit from "./Portit";
import Tarjoukset from "./Tarjous";
import Tarjousasetukset from "./Tarjousasetukset";
import Materiaalit from "./Materiaalit";
import Asiakkaat from "./Asiakkaat";
import Yrityksentiedot from "./Yrityksentiedot";
import Tarkastusnippu from "./Tarkastusnippu";
import Selailu from "./Selailu";
import Hitsikuittaus from "./Hitsikuittaus";
import Etenema from "./Etenema";
import Linjatarkastukset from "./Linjatarkastukset";
import Koeponnistusvalmius from "./Koeponnistusvalmius";
import Osaaminen from "./Osaaminen";
import Academy from "./Academy";
import Viestit from "./Viestit";
import KaannosTarkistus from "./KaannosTarkistus";
import { KohdeKontekstiTarjoaja, YlapalkinKohdevalinta } from "./Kohdekonteksti";
import "./tyylit.css";
import { UusiVersio } from "./Versio";

/* ═══════════════════════════════════════════════════════════
   Sovelluksen runko: kirjautuminen ja ylapalkki.
   ═══════════════════════════════════════════════════════════ */

/* ═══════════════════════════════════════════════════════════
   Kutsun lunastus.

   Julkinen sivu: saajalla ei ole vielä salasanaa, joten hän ei voi
   kirjautua. Linkin token on pääsy, ja se on kertakäyttöinen.
   ═══════════════════════════════════════════════════════════ */

function Kutsu({ token }: { token: string }) {
  const [brandi, setBrandi] = useState<Brandi | null>(null);
  useEffect(() => { void haeBrandi().then(setBrandi); }, []);
  const [tiedot, setTiedot] = useState<{ nimi: string; sahkoposti: string; olemassa: boolean } | null>(null);
  const [salasana, setSalasana] = useState("");
  const [uudelleen, setUudelleen] = useState("");
  const [virhe, setVirhe] = useState<string | null>(null);
  const [valmis, setValmis] = useState(false);
  const [ladataan, setLadataan] = useState(true);
  const [lahetetaan, setLahetetaan] = useState(false);

  useEffect(() => {
    api.kutsunTiedot(token)
      .then(setTiedot)
      .catch((v) => setVirhe(v instanceof ApiError ? virheTeksti(v.virhe, v.message) : t("main.linkki_ei_kelpaa")))
      .finally(() => setLadataan(false));
  }, [token]);

  async function laheta(e: React.FormEvent) {
    e.preventDefault();
    setVirhe(null);
    if (salasana !== uudelleen) { setVirhe(t("main.salasanat_eivat_tasmaa")); return; }
    if (salasana.length < 12) { setVirhe(t("main.salasana_12")); return; }
    setLahetetaan(true);
    try {
      await api.asetaSalasanaKutsulla(token, salasana);
      setValmis(true);
    } catch (v) {
      setVirhe(v instanceof ApiError ? virheTeksti(v.virhe, v.message) : t("tk.tallennus_virhe"));
      setLahetetaan(false);
    }
  }

  return (
    <div className="kirjautuminen">
      <div className="kirjautuminen__lomake">
        <Tunnus brandi={brandi} iso />

        {ladataan && <p className="dim">{t("main.tarkistetaan_linkkia")}</p>}

        {!ladataan && !tiedot && (
          <>
            <h1>{t("main.linkki_ei_kelpaa_otsikko")}</h1>
            <p className="kirjautuminen__virhe" role="alert">{virhe}</p>
            <a className="nappi nappi--kevyt" href="/">{t("main.kirjautumiseen")}</a>
          </>
        )}

        {valmis && (
          <>
            <h1>{tiedot?.olemassa ? t("main.salasana_vaihdettu") : t("main.salasana_asetettu")}</h1>
            <p className="dim">{tiedot?.olemassa ? t("main.vanha_ei_toimi") : t("main.voit_kirjautua")}</p>
            <a className="nappi nappi--paa" href="/">{t("kirjautuminen.kirjaudu")}</a>
          </>
        )}

        {!ladataan && tiedot && !valmis && (
          <form onSubmit={laheta} style={{ display: "contents" }}>
            {/* 122: sanamuoto seuraa tilaa — olemassa olevalle tunnukselle tämä on vaihto. */}
            <h1>{tiedot.olemassa ? t("main.vaihda_salasana") : t("main.aseta_salasana")}</h1>
            {tiedot.olemassa && <p className="dim">{t("main.on_jo_salasana")}</p>}
            <p className="dim">
              {tiedot.nimi} · <span className="mono">{tiedot.sahkoposti}</span>
            </p>

            <label>
              {t("kirjautuminen.salasana")}
              <input type="password" value={salasana} autoComplete="new-password"
                     required minLength={12} autoFocus
                     onChange={(e) => setSalasana(e.target.value)} />
            </label>
            <label>
              {t("main.salasana_uudelleen")}
              <input type="password" value={uudelleen} autoComplete="new-password" required
                     onChange={(e) => setUudelleen(e.target.value)} />
            </label>

            {virhe && <p className="kirjautuminen__virhe" role="alert">{virhe}</p>}

            <button type="submit" className="nappi nappi--paa" disabled={lahetetaan}>
              {lahetetaan ? t("pu.tallennetaan") : t("main.aseta_salasana")}
            </button>
            <p className="ikkuna__vihje">
              {t("main.kutsu_vihje")}
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

/* Selaimen otsikko seuraa nakymaa.
 *
 * OMANA KOMPONENTTINAAN eika efektina rungossa, ja se on koko
 * pointti: runko palaa aikaisin (`if (ladataan) return`,
 * `if (!kayttaja) return`), joten sen jalkeen sijoitettu hook
 * ajetaan vain osalla rendereista. React #310 -- "Rendered more
 * hooks than during the previous render" -- ja koko sovellus
 * kaatuu valkoiseen ruutuun.
 *
 * Niin kavi 29.8.2026 prototissa. Vika ei ollut otsikossa vaan
 * hookin paikassa, ja sama kuvio (aikainen return) oli kaatanut
 * saman paivan aikana jo PIN-paneelin.
 *
 * Komponentti ei renderoi mitaan; se vain omistaa yhden efektin.
 * Silloin hookien maara on vakio riippumatta siita mita runko
 * tekee.
 *
 * Ei kosmetiikkaa: sama merkkijono on tulosteen ylatunnisteessa ja
 * PDF:n tiedostonimessa. Ennen tata jokainen tuloste jokaisesta
 * nakymasta sanoi "Resurssit", koska otsikko tuli brandin
 * tuotenimesta. Brandin nimi jaa peraan, koska valilehden
 * tunnistettavuus nojaa siihen. */
/** 208 (Norjan erä 8.10.2026): instanssin merkintä käyttöliittymän kielellä; puuttuva englanninkielinen pari näyttää suomen. */
const merkintaKielella = (b: Brandi): string =>
  (kieli() !== "fi" && b.merkinta_en) ? b.merkinta_en : (b.merkinta ?? "");

function Otsikko({ nimi, brandi }: { nimi?: string; brandi: Brandi | null }) {
  useEffect(() => {
    // Nakyma · brandin nimi (Toni 10.9.): A7:n jalkeen ihmisilla on monta valilehtea
    // auki, ja «Sampo» yksinaan tekisi niista erottamattomia. Brandin nimi on A1:n
    // yksi lahde (brandi.nimi) — asiakkaan nimi, ei tyokalun. tuotenimi jaa
    // kirjautumissivun otsikoksi; valilehdessa se oli siemenen jaanne («Resurssit»).
    const brandinNimi = brandi?.nimi || "Sampo Industrial";
    const koko = brandi?.merkinta ? brandinNimi + " · " + merkintaKielella(brandi) : brandinNimi;
    document.title = nimi && nimi !== brandinNimi ? nimi + " · " + koko : koko;
  }, [nimi, brandi]);
  return null;
}

function Kirjautuminen({ valmis, syy }: { valmis: (k: Kayttaja) => void; syy?: string | null }) {
  const [brandi, setBrandi] = useState<Brandi | null>(null);
  useEffect(() => { void haeBrandi().then(setBrandi); }, []);
  // T439: kirjautumissivun kieli — tunnusta ei vielä tiedetä, joten valinta on tämän selaimen (kieli.ts).
  const [, setKieliVersio] = useState(0);
  asetaValittavat(brandi?.kielet);
  asetaKieli(null, brandi?.kieli, true);
  const [tila, setTila] = useState<"email" | "koodi" | "unohtunut">("email");
  const [palautusLahetetty, setPalautusLahetetty] = useState(false);
  const [sahkoposti, setSahkoposti] = useState("");
  const [salasana, setSalasana] = useState("");
  const [koodi, setKoodi] = useState("");
  const [pin, setPin] = useState("");
  const [virhe, setVirhe] = useState<string | null>(null);
  const [lahetetaan, setLahetetaan] = useState(false);

  async function laheta(e: React.FormEvent) {
    e.preventDefault();
    setVirhe(null);
    setLahetetaan(true);
    try {
      if (tila === "unohtunut") {
        // Vastaus on aina sama (ei paljasta tunnuksia); näytetään oma teksti eikä palvelimen, jotta kieli seuraa sivua.
        await api.unohtunutSalasana(sahkoposti, kieli() === "en" ? "en" : "fi");
        setPalautusLahetetty(true); setLahetetaan(false);
        return;
      }
      const { kayttaja } = tila === "koodi"
        ? await api.kirjauduKoodilla(koodi, pin)
        : await api.kirjaudu(sahkoposti, salasana);
      // Kirjautumissivulla juuri vaihdettu kieli tallennetaan tunnukselle (pääChat 11.10.2026); muuten tunnuksen oma voittaa.
      let vaihdettu: string | null = null;
      try { vaihdettu = sessionStorage.getItem("sampo.kieli.vaihdettu"); sessionStorage.removeItem("sampo.kieli.vaihdettu"); } catch { /* */ }
      if (vaihdettu && (kayttaja.kielet ?? []).includes(vaihdettu) && vaihdettu !== kayttaja.kieli) {
        try { await api.asetaKieli(vaihdettu); kayttaja.kieli = vaihdettu; } catch { /* tunnuksen kieli jää ennalleen */ }
      }
      valmis(kayttaja);
    } catch (v) {
      setVirhe(v instanceof ApiError ? virheTeksti(v.virhe, v.message) : t("kirjautuminen.ei_yhteytta"));
      setLahetetaan(false);
    }
  }

  return (
    <div className="kirjautuminen">
      <form className="kirjautuminen__lomake" onSubmit={laheta}>
        <Tunnus brandi={brandi} iso />
        {/* Tuotenimi kannasta, ei koodista: "Resurssit" on Pro-Totin
            nimi, ja se luki jokaisen asiakkaan kirjautumissivulla. */}
        <h1>{tila === "koodi" ? t("kirjautuminen.kenttatili") : tila === "unohtunut" ? t("kirjautuminen.unohtunut_otsikko") : (brandi?.tuotenimi ?? "Sampo Industrial")}</h1>

        {/* Miksi olet täällä. Ilman tätä paluu kirjautumiseen on yhtä hiljainen
            kuin uloskirjaus oli, ja käyttäjä lukee sen satunnaisuutena. */}
        {syy && <p className="kuittaus-ok" data-istunto-syy="">{syy}</p>}

        {tila === "unohtunut" ? (
          palautusLahetetty
            ? <p className="huom" style={{ color: "var(--muste)" }} role="status" data-palautus-lahetetty="">{t("kirjautuminen.unohtunut_lahetetty")}</p>
            : <>
                <p className="huom">{t("kirjautuminen.unohtunut_ohje")}</p>
                <label>
                  {t("kirjautuminen.sahkoposti")}
                  <input type="email" value={sahkoposti} autoComplete="username" required autoFocus
                         onChange={(e) => setSahkoposti(e.target.value)} data-palautus-sahkoposti="" />
                </label>
              </>
        ) : tila === "email" ? (
          <>
            <label>
              {t("kirjautuminen.sahkoposti")}
              <input
                type="email" value={sahkoposti} autoComplete="username" required autoFocus
                onChange={(e) => setSahkoposti(e.target.value)}
              />
            </label>
            <label>
              {t("kirjautuminen.salasana")}
              <input
                type="password" value={salasana} autoComplete="current-password" required
                onChange={(e) => setSalasana(e.target.value)}
              />
            </label>
          </>
        ) : (
          <>
            <label>
              {t("kirjautuminen.koodi")}
              <input
                type="text" value={koodi} autoComplete="username" required autoFocus
                autoCapitalize="characters" placeholder={t("kirjautuminen.koodi_esim")}
                onChange={(e) => setKoodi(e.target.value)}
              />
            </label>
            <label>
              {t("kirjautuminen.pin")}
              <input
                type="password" inputMode="numeric" value={pin} autoComplete="current-password" required
                onChange={(e) => setPin(e.target.value)}
              />
            </label>
          </>
        )}

        {virhe && <p className="kirjautuminen__virhe" role="alert">{virhe}</p>}

        {!(tila === "unohtunut" && palautusLahetetty) && (
          <button type="submit" className="nappi nappi--paa" disabled={lahetetaan} data-kirjautuminen-laheta="">
            {tila === "unohtunut" ? t("kirjautuminen.unohtunut_laheta")
              : lahetetaan ? t("kirjautuminen.kirjaudutaan") : t("kirjautuminen.kirjaudu")}
          </button>
        )}

        {tila === "email" && (
          <button type="button" className="tekstilinkki" style={{ marginTop: 8 }} data-unohtunut-linkki=""
            onClick={() => { setTila("unohtunut"); setVirhe(null); setPalautusLahetetty(false); }}>
            {t("kirjautuminen.unohtunut")}
          </button>
        )}

        <button type="button" className="nappi nappi--kevyt" style={{ marginTop: 8 }}
          onClick={() => { setTila(tila === "email" ? "koodi" : "email"); setVirhe(null); setPalautusLahetetty(false); }}>
          {tila === "email" ? t("kirjautuminen.kenttatilille") : t("kirjautuminen.sahkopostilla")}
        </button>

        {/* T439: kielen valinta ennen kirjautumista — tähän selaimeen, ei tunnukselle. Kielen nimi omalla kielellään. */}
        <div className="kirjautuminen__kieli" data-kirjautumisen-kieli={kieli()} role="group" aria-label={t("kieli.otsikko")}>
          {valittavatKielet().map((k) => (
            <button key={k} type="button" lang={k} data-kieli={k} aria-pressed={kieli() === k}
              className={"tekstilinkki" + (kieli() === k ? " tekstilinkki--aktiivinen" : "")}
              onClick={() => { asetaSelaimenKieli(k); try { sessionStorage.setItem("sampo.kieli.vaihdettu", k); } catch { /* */ }
                               setKieliVersio((x) => x + 1); }}>
              {KIELEN_NIMI[k]}
            </button>
          ))}
        </div>
      </form>
    </div>
  );
}

/** Tunnus ylapalkkiin ja kirjautumissivulle. Yksi paikka, jotta
 *  brändin vaihto ei jää puolitiehen johonkin näkymään. */
function Tunnus({ brandi, iso }: { brandi: Brandi | null; iso?: boolean }) {
  /*
   * Kuva jos asiakkaalla on logo, typografinen merkki jos ei.
   *
   * Varareitti ei ole varovaisuutta: `brandi.logo` osoittaa polkuun
   * /config/brandi/logo.svg JOKAISESSA instanssissa migraatiosta 004
   * asti, eikä useimmilla ole sitä tiedostoa. Ilman onError-paluuta
   * ne näyttäisivät rikkinäisen kuvan ikonia.
   *
   * Tumma ylapalkki tarvitsee vaalean version. `logo_tumma` on
   * valinnainen: null = käytetään samaa.
   */
  const [kuvaPetti, setKuvaPetti] = useState(false);
  /*
   * `iso` on kirjautumissivu, jonka kortti on VAALEA riippumatta
   * ylapalkin väristä. `brandi.ylapalkki_tumma` kertoo nimensä
   * mukaisesti vain ylapalkista, joten sitä ei saa lukea täällä:
   * AaltoFlow'lla lippu on true, ja kirjautumissivulle valikoitui
   * valkoinen logo vaalealle kortille — käytännössä näkymätön.
   * Löytyi vasta kuvasta, ei koodia lukemalla.
   */
  const tummaTausta = iso ? false : (brandi?.ylapalkki_tumma ?? true);
  const polku = (tummaTausta ? brandi?.logo_tumma : null) || brandi?.logo || null;

  /*
   * Merkintä (esim. DEMO) piirtyy KUMMANKIN haaran vieressä.
   *
   * Ensimmäinen versio laittoi sen vain typografiseen haaraan, jolloin
   * oman logon asettanut instanssi olisi menettänyt merkinnän juuri
   * siinä tapauksessa jossa se on eniten tarpeen — brändätty demo
   * näyttää eniten tuotannolta. Portti erehtyy näkyvään suuntaan.
   */
  const merkinta = brandi?.merkinta
    ? <span className={"palkki__merkinta" + (iso ? " palkki__merkinta--iso" : "")}>
        {merkintaKielella(brandi)}
      </span>
    : null;

  if (polku && !kuvaPetti) {
    return (
      <>
        <img className={"palkki__logo-kuva" + (iso ? " palkki__logo-kuva--iso" : "")}
             src={polku} alt={brandi?.nimi ?? "Sampo Industrial"}
             onError={() => setKuvaPetti(true)} />
        {merkinta}
      </>
    );
  }

  return (
    <>
      <div className={"palkki__logo" + (iso ? " palkki__logo--iso" : "")}
           aria-label={brandi?.nimi ?? "Sampo Industrial"}>
        <span className="palkki__logo-nimi">{brandi?.tunnus ?? "SAMPO"}</span>
        {brandi?.tunnus_ala && (
          <span className="palkki__logo-ala">{brandi.tunnus_ala}</span>
        )}
      </div>
      {merkinta}
    </>
  );
}

function Salasana({ sulje }: { sulje: () => void }) {
  const [nykyinen, setNykyinen] = useState("");
  const [uusi, setUusi] = useState("");
  const [uusiUudelleen, setUusiUudelleen] = useState("");
  const [virhe, setVirhe] = useState<string | null>(null);
  const [valmis, setValmis] = useState(false);
  const [lahetetaan, setLahetetaan] = useState(false);

  async function laheta(e: React.FormEvent) {
    e.preventDefault();
    setVirhe(null);
    if (uusi !== uusiUudelleen) { setVirhe(t("main.uudet_eivat_tasmaa")); return; }
    if (uusi.length < 12) { setVirhe(t("main.uusi_12")); return; }
    setLahetetaan(true);
    try {
      await api.vaihdaSalasana(nykyinen, uusi);
      setValmis(true);
    } catch (v) {
      setVirhe(v instanceof ApiError ? virheTeksti(v.virhe, v.message) : t("main.vaihto_virhe"));
      setLahetetaan(false);
    }
  }

  return (
    <div className="peite" onClick={(e) => { if (e.target === e.currentTarget) sulje(); }}>
      <section className="panel lomake peite__sisalto">
        <div className="panel__head">
          <h2>{t("main.vaihda_salasana")}</h2>
          <button className="ikkuna__sulje" onClick={sulje} aria-label={t("hl.sulje")}>×</button>
        </div>

        {valmis ? (
          <div className="lomake__kentat">
            <p className="huom">
              {t("main.vaihdettu_huom")}
            </p>
            <button className="nappi nappi--paa" onClick={sulje}>{t("hl.sulje")}</button>
          </div>
        ) : (
          <form className="lomake__kentat" onSubmit={laheta}>
            <label className="kasvaa">
              {t("main.nykyinen")}
              <input type="password" value={nykyinen} autoComplete="current-password" required
                     onChange={(e) => setNykyinen(e.target.value)} />
            </label>
            <label className="kasvaa">
              {t("main.uusi")}
              <input type="password" value={uusi} autoComplete="new-password" required minLength={12}
                     onChange={(e) => setUusi(e.target.value)} />
            </label>
            <label className="kasvaa">
              {t("main.uusi_uudelleen")}
              <input type="password" value={uusiUudelleen} autoComplete="new-password" required
                     onChange={(e) => setUusiUudelleen(e.target.value)} />
            </label>
            <div className="lomake__toiminnot">
              <button type="submit" className="nappi nappi--paa" disabled={lahetetaan}>
                {lahetetaan ? t("main.vaihdetaan") : t("main.vaihda")}
              </button>
              <button type="button" className="nappi nappi--kevyt" onClick={sulje}>{t("hl.peruuta")}</button>
            </div>
            {virhe && <p className="huom huom--vika" role="alert">{virhe}</p>}
            <p className="ikkuna__vihje">
              {t("main.vaihto_vihje")}
            </p>
          </form>
        )}
      </section>
    </div>
  );
}

/** Kaksi näkymää: reititin tulee kun niitä on enemmän. */
/*
 * Nakymien tunnukset AJONAIKAISENA listana, ja tyyppi johdetaan
 * siita. Ennen tama oli pelkka tyyppiunioni, joka katoaa
 * kaannoksessa -- eika osoitteesta luettua merkkijonoa voinut siis
 * verrata mihinkaan. Yksi lahde, ei kahta listaa jotka ajautuvat
 * erilleen; sama muoto kuin MODUULIT api-puolella.
 */
const NAKYMAT = [
  "tarjous", "resurssit", "kohteet", "mitoitus", "hitsausloki",
  "selailu", "etenema",
  // T646 (1) ja T514 (Toni 23.10.2026 klo 05.35): seuranta ja raportointi.
  "linjatarkastukset", "koeponnistusvalmius",
  "henkilot", "patevyydet", "kalusto", "kayttajat",
  "tarjousasetukset", "materiaalit", "asiakkaat", "yritys", "rotaatio",
  "hub", "aukot", "puutteet", "tyopaketit", "varasto", "ainestodistukset",
  "minun", "tuntikirjaus", "osaaminen", "academy", "laiteasennus", "iv_asennus", "portit", "viestit",
  // 206 (T440): käännösten tarkistus.
  "kaannokset",
  // Toni 10.10.2026: demotestauksen ohje (vain TESTAUS=1-instanssissa).
  "testaus",
  // T481 (Toni 11.10.2026): pysyvä roolin ohje kaikissa instansseissa.
  "ohjeet",
  // T487 (Toni 11.10.2026): pääkäyttäjän koonti demon testaajien palautteesta (vain TESTAUS=1).
  "testauspalaute",
  // 198 (päätös 90): linjatarkastusnippu.
  "tarkastusnippu",
  // 172: QR-tarran skannaus (#/k/<n>). Ei valikkokohta: tarra on osoite, ei navigointia.
  "k",
  // 213 (T445): puuttuvien hitsien kuittaus työnjohdolle.
  "kuittaus",
  // T373: kolmen viikon suunnitelma.
  "suunnitelma",
  // T488 (Toni 11.10.2026): tilaajan valvojan jono (oikeus punakyna_hyvaksynta) — Selailun jono ilman muuta Selailua.
  "vahvistettavat",
] as const;
type Nakyma = (typeof NAKYMAT)[number];

/* LASKEUTUMISSIVU ON PROJEKTI-HUB, EI RESURSSIT.
 *
 * Tässä luki `"resurssit"` ilman perustelua — se oli ensimmäinen
 * näkymä jonka tuote sai, ja se jäi oletukseksi siksi ettei kukaan
 * vaihtanut sitä.
 *
 * Hub on koostenäkymä joka vastaa kysymykseen "miten tämä projekti
 * voi"; Resurssit vastaa kysymykseen "kuka on missä". Ensimmäinen on
 * se jonka työnjohtaja avaa aamulla, ja se on ainoa näkymä joka
 * kertoo yhdellä silmäyksellä mitä muissa näkymissä on.
 *
 * ⚠ Tämä EI koske sitä joka ei näe Hubia: rivi 666 palauttaa
 * ensimmäisen NÄKYVÄN navikohdan eikä tätä vakiota, joten
 * hitsaajatunnus laskeutuu edelleen omaan ensimmäiseen kohtaansa.
 * Oletus on oletus, ei pakko.
 *
 * Mitattu 2.9.2026: aamun oletuskohde-korjaus (Hub avaa sisällön
 * mukaan eikä listan ensimmäistä) ei näkynyt kenellekään, koska
 * kukaan ei laskeutunut Hubiin. Korjaus oli oikea ja näkymätön. */
const OLETUSNAKYMA: Nakyma = "hub";

/*
 * Nakyma osoitteen hash-osasta: `#/selailu`.
 *
 * Hash eika polku, koska palvelin tarjoilee yhden index.html:n eika
 * tieda naista poluista mitaan -- `/selailu` antaisi 404:n
 * uudelleenlatauksessa. Hash ei myoskaan tormaa kutsulinkkiin, joka
 * lukee `window.location.pathname`ia.
 *
 * Tuntematon hash EI kaada mitaan vaan palauttaa oletuksen. Portti
 * erehtyy kiinni pain, kuten `paalla()` ja `vaadiModuuli()`.
 */
/*
 * OSOITESAANTO: polku on identiteetti, kysely on tila.
 *
 *   #/selailu                         nakyma
 *   #/selailu/100-0011-SL-80-...      nakyma + se yksi asia joka on auki
 *
 * Polkuun paasee vain nakyma ja avattu kohde. Suodatin, haku,
 * jarjestys ja avatut paneelit EIVAT ole polussa: ne ovat tilaa, ei
 * identiteettia, eika tilaa ole mielekasta jakaa kenellekaan.
 *
 * Tunnus on ASIAKKAAN oma, ei meidan id:mme. Se tulee isometrista ja
 * lukija tunnistaa sen ilman selitysta. Toinen numerointi asiakkaan
 * oman rinnalle on juuri se mita ei tehda.
 *
 * ⚠ `UNIQUE (kohde_id, tunnus)` -- tunnus on uniikki KOHTEEN sisalla,
 * ei globaalisti. Mitattu 31.8.2026: nolla tormaysta kaikissa
 * neljassa (844 linjaa). Tormays on siis mahdollinen mutta ei
 * esiinny, ja se kasitellaan poikkeuksena nakymassa -- osoitemallia
 * ei rakenneta sen ymparille.
 *
 * TÄSMENNYS 3.9.2026 -- kyselyosa on JOUKON identiteettiä:
 *
 *   #/selailu?nayta=punchit&kohde=32&vain_avoimet=1
 *   #/puutteet?kohde_id=32&tila=avoimet&tyyppi=lahdeaukko&vaikutus=hidastaa
 *   #/aukot?kohde=32&ulottuvuus=loki
 *   #/hub/N-2410
 *
 * Projekti-Hubin jokainen luku on linkki listaan jossa on TÄSMÄLLEEN
 * sama joukko, ja se joukko on määritelty suodattimella. Suodatin
 * joka määrittelee luvun ei ole tilaa jota joku säätää, se on osa
 * väitettä "48 avointa punchia" -- ja väitteen on oltava
 * lähetettävissä ja F5:n palautettava se. Siksi se on osoitteessa.
 * Nimet ovat API:n omat (`kohde_id`, `vain_avoimet`, `tila`), jotta
 * osoite on luettavissa suoraan kutsuksi. Hakukenttä, järjestys ja
 * avatut paneelit ovat yhä tilaa eivätkä mene osoitteeseen.
 * Mekanismi: `osoitehaku.ts`.
 *
 * Hubin oma kohde on polussa (`#/hub/N-2410`) kuten Selailun linja:
 * se on se yksi asia joka on auki, asiakkaan omalla tunnuksella
 * (`kohde.tunnus` on globaalisti uniikki, 001).
 */
function osoitteenOsat(): { nakyma: Nakyma; kohde: string | null; haku: string } {
  const koko = window.location.hash.replace(/^#\/?/, "");
  // Kyselyosa erotetaan ENNEN dekoodausta: polkuun koodattu `?`
  // (`%3F`) on osa tunnusta, paljas `?` aloittaa kyselyn.
  const kysymys = koko.indexOf("?");
  const h = kysymys < 0 ? koko : koko.slice(0, kysymys);
  const haku = kysymys < 0 ? "" : koko.slice(kysymys + 1);
  const osat = h.split("/");
  const n = osat[0] ?? "";
  const nakyma = (NAKYMAT as readonly string[]).includes(n)
    ? (n as Nakyma) : OLETUSNAKYMA;
  const hanta = osat.slice(1).join("/");
  return { nakyma, kohde: hanta ? decodeURIComponent(hanta) : null, haku };
}

/*
 * T439: valikon tekstit käyttäjän kielellä. Suomenkielinen otsikko ja nimi pysyvät valikon TUNNISTEINA (ryhmän auki/kiinni-tila
 * tallentuu `ryhmanTunnus(otsikko)`:lla palvelimelle, ja kävely etsii kohtia niillä), joten käännös tehdään vasta piirrossa.
 * Kartassa puuttuva nimi näytetään sellaisenaan — ei tyhjää.
 */
const VALIKON_KAANNOS: Record<string, KaannosAvain> = {
  "Tarkastus": "valikko.ryhma.tarkastus", "Kenttätyö": "valikko.ryhma.kenttatyo", "Projekti": "valikko.ryhma.projekti",
  "Myynti": "valikko.ryhma.myynti", "Resursointi": "valikko.ryhma.resursointi", "Väki & kalusto": "valikko.ryhma.vaki",
  "Tuotanto": "valikko.ryhma.tuotanto", "Hallinta": "valikko.ryhma.hallinta",
  "Seuranta ja raportointi": "valikko.ryhma.seuranta", "Luovutusdokumentaatio": "valikko.ryhma.luovutusdokumentaatio",
  "Academy": "valikko.academy", "Ainestodistukset": "valikko.ainestodistukset", "Asiakkaat": "valikko.asiakkaat",
  "Etenemä": "valikko.etenema", "Linjatarkastukset": "valikko.linjatarkastukset", "Valmiina koeponnistukseen": "valikko.koeponnistusvalmius",
"Henkilöstö": "valikko.henkilosto", "Hitsausloki": "valikko.hitsausloki",
  "IV-asennus": "valikko.iv_asennus", "Portit (TG)": "valikko.portit","Kalusto": "valikko.kalusto", "Laiteasennus": "valikko.laiteasennus",
  "Linjat ja tarkastukset": "valikko.linjat_ja_tarkastukset", "Linjatarkastus": "valikko.linjatarkastus",
  "Luovutusvalmius": "valikko.luovutusvalmius", "Materiaalit": "valikko.materiaalit", "Mitoitus": "valikko.mitoitus",
  "Omat tiedot": "valikko.omat_tiedot", "Osaamisradat": "valikko.osaamisradat", "Projekti-Hub": "valikko.projekti_hub",
  "Puutteet": "valikko.puutteet", "Puuttuvat hitsit": "valikko.puuttuvat_hitsit", "Pätevyydet": "valikko.patevyydet", "Resurssit": "valikko.resurssit",
  "Selailu": "valikko.selailu", "Tarjoukset": "valikko.tarjoukset", "Tarjousasetukset": "valikko.tarjousasetukset",
  "Tunnukset": "valikko.tunnukset", "Tuntikirjaus": "valikko.tuntikirjaus", "Työkohteet": "valikko.tyokohteet",
  "Työpaketit": "valikko.tyopaketit", "Työvuorot": "valikko.tyovuorot", "Varasto": "valikko.varasto",
  "Viestit": "valikko.viestit", "Yrityksen tiedot": "valikko.yrityksen_tiedot", "Käännökset": "valikko.kaannokset", "Testaus": "valikko.testaus", "Ohjeet": "valikko.ohjeet",
  "Tuki": "valikko.ryhma.tuki", "Testauspalaute": "valikko.testauspalaute",
  "Odottaa vahvistusta": "valikko.odottaa_vahvistusta",
  // Toni 15.10.2026: puuttui kartasta alusta asti, joten englanninkielinen valikko näytti suomen («3 viikon suunnitelma» → nyt
  // «3-viikkoissuunnitelma»). Käännösportti tarkistaa nyt, että jokaisella valikon nimellä on karttarivi.
  "3-viikkoissuunnitelma": "valikko.suunnitelma",
};
/** Roolin näyttönimi (11.10.2026: estosivu näytti koodit «kenttatekija, oppija, paakayttaja»). Tuntematon koodi sellaisenaan. */
const ROOLIN_NIMI: Record<string, KaannosAvain> = {
  paakayttaja: "rooli.paakayttaja", suunnittelija: "rooli.suunnittelija", tyonjohtaja: "rooli.tyonjohtaja",
  kenttatekija: "rooli.kenttatekija", lukija: "rooli.lukija", tarkastaja: "rooli.tarkastaja", oppija: "rooli.oppija",
};
const roolinNimi = (r: string): string => (ROOLIN_NIMI[r] ? t(ROOLIN_NIMI[r]!) : r);
const valikonTeksti = (fi: string | undefined): string | undefined =>
  fi === undefined ? undefined : (VALIKON_KAANNOS[fi] ? t(VALIKON_KAANNOS[fi]!) : fi);

function Sovellus() {
  const [kayttaja, setKayttaja] = useState<Kayttaja | null>(null);
  const [istuntoSyy, setIstuntoSyy] = useState<string | null>(null);
  /* Oliko käyttäjä tällä sivulla kirjautuneena (8.10.2026, pääChatin toimeksianto Tonin välittämänä): «Istunto vanhentui»
     näkyi myös oman uloskirjautumisen jälkeen ja jokaisella kirjautumattomalla latauksella, koska jokainen 401 — myös sivun
     ensimmäinen /api/mina ilman istuntoa ja uloskirjautumisen jälkeiset taustapyynnöt — laukaisi viestin. Nyt viesti
     tulee vain kun kirjautuneen istunto katoaa kesken käytön. Ref eikä tila: tapahtumankäsittelijä lukee nykyarvon. */
  const kirjautunut = useRef(false);
  useEffect(() => { kirjautunut.current = kayttaja !== null; }, [kayttaja]);
  // Toni 10.10.2026: uloskirjautuminen pyyhkii hitsauslokin istunnon — seuraava kirjautuja aloittaa asetuslomakkeesta.
  const ulos = () => { kirjautunut.current = false; setIstuntoSyy(null); tyhjennaHitsauslokinIstunto();
                       void api.kirjauduUlos().finally(() => setKayttaja(null)); };
  const [ladataan, setLadataan] = useState(true);
  const [nakyma, setNakyma] = useState<Nakyma>(() => osoitteenOsat().nakyma);
  /** 162: lukemattomat viestit (punainen piste). */
  const [lukematta, setLukematta] = useState(0);
  const [tehtavia, setTehtavia] = useState(0);   // T534 jatko: omat avoimet tehtävät navin Omat tiedot -kohtaan
  const [osoiteKohde, setOsoiteKohde] =
    useState<string | null>(() => osoitteenOsat().kohde);
  const [osoiteHaku, setOsoiteHaku] =
    useState<string>(() => osoitteenOsat().haku);

  /*
   * Osoite seuraa nakymaa ja nakyma osoitetta.
   *
   * ⚠ Nama hookit ovat TASSA eivatka lahempana kayttokohtaansa,
   * koska rivilla ~484 on `if (ladataan) return` ja `if (!kayttaja)
   * return`. Hook niiden jalkeen ajaa vain toisella haaralla, ja
   * juuri se kaatoi nelja instanssia 41 tunniksi 29.8.2026
   * (React #310). `react-hooks/rules-of-hooks` kaataa nyt
   * kaannoksen jos joku siirtaa naita alemmas -- mutta saanto on
   * portti, ei syy: syy on etta hookit kuuluvat rungon alkuun.
   */
  useEffect(() => {
    const h = "#/" + nakyma + (osoiteKohde ? "/" + encodeURIComponent(osoiteKohde) : "")
            + (osoiteHaku ? "?" + osoiteHaku : "");
    // Kirjoitetaan vain jos eroaa: muuten hashchange laukeaisi
    // kuuntelijan, joka asettaisi saman arvon, ja silmukka jaisi
    // pyorimaan. Reactin oma bail-out riittaisi, mutta osoiterivi
    // vilkkuisi.
    if (window.location.hash !== h) window.location.hash = h;
  }, [nakyma, osoiteKohde, osoiteHaku]);

  useEffect(() => {
    // Selaimen takaisin-nappi ja kasin kirjoitettu osoite.
    const kuuntele = () => {
      const o = osoitteenOsat();
      setNakyma(o.nakyma); setOsoiteKohde(o.kohde); setOsoiteHaku(o.haku);
    };
    window.addEventListener("hashchange", kuuntele);
    return () => window.removeEventListener("hashchange", kuuntele);
  }, []);
  const [salasana, setSalasana] = useState(false);
  // Kapealla ruudulla navigaatio ei mahdu riville. Auki/kiinni
  // pelkästään mobiilissa; leveällä valikko on aina näkyvissä.
  const [valikkoAuki, setValikkoAuki] = useState(false);
  // Valikkoryhmät (T539, Toni 12.10.2026 «kyllä, kirjaa max 2 auki» ja 13.10.2026 kuva: kolme ryhmää auki): auki on enintään
  // kaksi — nykyisen sivun ryhmä ja viimeksi avattu. Uuden avaus sulkee edellisen avatun; nykyisen sivun ryhmää ei suljeta
  // toisen avauksella. Aloitusnäkymässä ja latauksen jälkeen auki vain nykyisen sivun ryhmä, joten valintaa ei enää tallenneta
  // (kumoaa 25.9. «useampi ryhmä saa olla auki» ja 170:n tallennuksen valikolle; muut näyttötilat ennallaan).
  // Hookit tässä eikä alempana: runko palaa aikaisin (ladataan / kirjautuminen), ja hook sen jälkeen kaataa Reactin (#310).
  // 15.10.2026 (Toni): aktiivisen sivun ryhmä on AINA auki («oma kiinni» -tila poistui), ja itse avattu ryhmä säilyy sivun vaihdossa ja
  // uudelleenlatauksessa — selaimen oma muisti (localStorage), luku ja kirjoitus try/catchissa: puuttuva tai estetty muisti on
  // «ei avattua», ei virhe. Enintään kaksi auki (T539) pysyy: oma + yksi avattu.
  const [avattuRyhma, setAvattuRyhmaTila] = useState<string | null>(() => {
    try { return localStorage.getItem("sampo.valikko.avattuRyhma") || null; } catch { return null; }
  });
  const setAvattuRyhma = (r: string | null) => {
    setAvattuRyhmaTila(r);
    try { if (r) localStorage.setItem("sampo.valikko.avattuRyhma", r); else localStorage.removeItem("sampo.valikko.avattuRyhma"); } catch { /* ei muistia */ }
  };
  const [moduulit, setModuulit] = useState<Moduuli[]>([]);
  const [brandi, setBrandi] = useState<Brandi | null>(null);

  // Brändi ennen ensimmäistä piirtoa: värit asettuvat :root-tasolle
  // ja ylapalkki saa nimensä kannasta, ei koodista.
  useEffect(() => { void haeBrandi().then(setBrandi); }, []);
  // T439: kieli ennen lasten piirtoa — käyttäjän oma, sitten selaimen kirjautumisvalinta, sitten instanssin (kieli.ts).
  // Ei hook: tavallinen lause, joten se saa olla myös aikaisten paluiden yläpuolella.
  asetaValittavat(kayttaja?.kielet ?? brandi?.kielet);
  asetaKieli(kayttaja?.kieli ?? null, brandi?.kieli);
  // Kielen vaihto yläpalkista tai Omista tiedoista (pääChat 11.10.2026): ei uudelleenlatausta — tunnuksen kieli päivittyy ja
  // sovelluksen runko piirtyy uudelleen (avain), jotta jokainen näkymä hakee tekstinsä uudella kielellä.
  const [kieliAvain, setKieliAvain] = useState(0);
  useEffect(() => {
    const kuuntele = (e: Event) => {
      const k = (e as CustomEvent<string | null>).detail;
      setKayttaja((x) => (x ? { ...x, kieli: k } : x));
      setKieliAvain((n) => n + 1);
    };
    window.addEventListener("sampo-kieli", kuuntele);
    return () => window.removeEventListener("sampo-kieli", kuuntele);
  }, []);

  // Onko istunto voimassa entuudestaan
  useEffect(() => {
    api.mina()
      .then(({ kayttaja }) => setKayttaja(kayttaja))
      .catch(() => setKayttaja(null))
      .finally(() => setLadataan(false));
  }, []);

  /*
   * Istunto voi vanheta kesken käytön, ja silloin ruutu palaa kirjautumiseen
   * nimetysti — ei jää tyhjäksi listaksi. Ilman tätä 401 hukkui näkymän omaan
   * catch-lohkoon ja käyttäjä luki sen «data on poissa» (mitattu 19.9.2026).
   */
  useEffect(() => {
    const vanhentui = () => {
      if (!kirjautunut.current) return;   // ei kirjautunut tai kirjautui itse ulos: ei vanhenemista
      kirjautunut.current = false;
      setKayttaja(null); setIstuntoSyy(t("kirjautuminen.istunto_vanhentui"));
    };
    window.addEventListener("sampo:istunto-vanhentui", vanhentui);
    return () => window.removeEventListener("sampo:istunto-vanhentui", vanhentui);
  }, []);

  // Moduulit ratkaisevat mitä navigaatiossa näkyy.
  useEffect(() => {
    if (!kayttaja) return;
    api.moduulit().then(setModuulit).catch(() => setModuulit([]));
  }, [kayttaja]);

  // Hitsaaja (vain se rooli) ohjataan suoraan hitsauslokiin — hänellä
  // ei ole muuta näkymää.
  /*
   * Ohjaus ei saa osoittaa moduuliin joka on pois päältä.
   *
   * Aiemmin oppija ohjattiin aina Academyyn ja hitsaaja aina
   * hitsauslokiin, kysymättä onko moduuli käytössä. Kun AaltoFlow'sta
   * sammutettiin hitsausloki, sen hitsaajatunnus laskeutui tyhjään
   * ruutuun: näkymä on `paalla`-vartioitu, ohjaus ei ollut.
   *
   * `paalla()` EI kelpaa tähän: se on latauksen aikana tosi jottei
   * valikko vilku tyhjänä, ja tämä efekti voi ajaa ennen kuin
   * `/api/moduulit` on vastannut. Siksi luetaan `moduulit` suoraan ja
   * odotetaan että lista on olemassa.
   *
   * `minun` on varasuunnitelma koska se on ainoa näkymä jolla ei ole
   * moduulia lainkaan — se ei voi kadota lipun alta.
   */
  useEffect(() => {
    if (!kayttaja || moduulit.length === 0) return;
    // 172: skannattu tarra (#/k/<n>) voittaa aloitusnäkymän. Ilman tätä kenttätekijä
    // kirjautui tarran kautta ja päätyi Hitsauslokiin — kävely mittasi sen 26.9.2026.
    if (osoitteenOsat().nakyma === "k") return;
    const kaytossa = (t: ModuuliTunnus) =>
      moduulit.some((m) => m.tunnus === t && m.kaytossa);
    const eiMuutaRoolia = (paitsi: string[]) =>
      !kayttaja.roolit.some((r) => paitsi.includes(r));

    if (kayttaja.roolit.includes("oppija")
        && eiMuutaRoolia(["paakayttaja", "suunnittelija", "tyonjohtaja",
                          "lukija", "kenttatekija"])) {
      setNakyma(kaytossa("osaaminen") ? "academy" : "minun");
      return;
    }
    if (kayttaja.roolit.includes("kenttatekija")
        && eiMuutaRoolia(["paakayttaja", "suunnittelija", "tyonjohtaja", "lukija"])) {
      setNakyma(kaytossa("hitsausloki") && kayttaja.hitsaustyo ? "hitsausloki" : "minun");
    }
  }, [kayttaja, moduulit]);

  /**
   * Näkyykö moduuli tälle asiakkaalle.
   *
   * `GET /api/moduulit` palauttaa vain `myyty`-rivit, joten myymätön
   * moduuli puuttuu listasta kokonaan. Aiempi `?? true` päätteli siitä
   * "näytetään" — eli asiakas näki valikossa rivin ominaisuudelle jota
   * hän ei ole ostanut, ja klikkaus päätyi 404:ään. Data ei vuotanut
   * (API on portitettu), mutta valikko lupasi jotain mitä ei ole.
   *
   * Portti erehtyy nyt kiinni päin kuten palvelimella (PAATOKSET
   * §Portti erehtyy kiinni päin). Tunnus on tyyppi eikä merkkijono,
   * jotta kirjoitusvirhe ei piilottaisi navi-kohtaa hiljaa.
   *
   * Tyhjä lista on eri asia kuin puuttuva tunnus: se on lataustila
   * (tai epäonnistunut haku) ennen kuin vastaus on tullut, eikä
   * käyttöliittymä saa vilkkua tyhjänä sen ajan.
   */
  /* 162: lukemattomat viestit navin punaiseen pisteeseen. Haetaan kirjautuessa,
     näkymän vaihtuessa ja minuutin välein — ei pushia (Toni 22.9.). */
  const haeLukematta = useCallback(() => {
    if (!kayttaja) return;
    // 10.10.2026 (Toni): Viestit pois tilaajalta — ei lukemattomien kyselyä (reitti 403).
    if (kayttaja.laji === "ulkopuolinen" && !kayttaja.roolit.includes("tarkastaja")) return;
    api.viestejaLukematta().then((r) => setLukematta(r.lukematta)).catch(() => {});
    // T534 jatko: sama rytmi kuin viesteillä — kirjautuessa, näkymän vaihtuessa ja minuutin välein.
    api.omiaTehtavia().then((r) => setTehtavia(r.n)).catch(() => {});
  }, [kayttaja]);
  useEffect(() => {
    haeLukematta();
    const t = setInterval(haeLukematta, 60000);
    return () => clearInterval(t);
  }, [haeLukematta, nakyma]);

  const paalla = (tunnus: ModuuliTunnus) =>
    moduulit.length === 0 || (moduulit.find((m) => m.tunnus === tunnus)?.kaytossa ?? false);

  if (ladataan) return <div className="lataa">{t("ltar.ladataan")}</div>;
  if (!kayttaja) return <Kirjautuminen valmis={(k) => { setIstuntoSyy(null); setKayttaja(k); }} syy={istuntoSyy} />;
  // 168: pääkäyttäjän asettama PIN vaihdetaan ennen mitään muuta (palvelin pitää saman portin).
  if (kayttaja.pinVaihdettava) {
    return <PinVaihto kayttaja={kayttaja} valmis={() => api.mina().then((r) => setKayttaja(r.kayttaja))} />;
  }
  /* 172: tarran skannaus avaa oman puhelinnäkymänsä kenelle tahansa kirjautuneelle — valikko ei
     päätä sitä, API päättää (kenttätili näkee, tilaaja 403). Kirjautumaton on jo pysähtynyt
     yllä kirjautumisseinään (K2 a), ja osoite #/k/<n> on säilynyt sen yli. */
  // T589 (17.10.2026): puhelinnäkymä saa saman ytimen kohdekontekstin kuin muut näkymät, jotta lomakkeen oletuskohde on tunnuksen
  // valitsema kohde. Ilman tarjoajaa useKohde() palautti oletuskontekstin, eikä oletus voinut koskaan asettua (kävely 16.10.: kenttä puuttuu).
  if (nakyma === "k") return <KohdeKontekstiTarjoaja><KalustoQr kayttaja={kayttaja} numero={osoiteKohde} /></KohdeKontekstiTarjoaja>;

  // Navigaatio ryhmiteltynä: ei tarvitse silmäillä koko listaa
  // löytääkseen. Kohta näkyy jos sen moduuli on myyty ja käytössä ja
  // rooli riittää.
  // Kenttätekijälle kentän näkymät; muut näkevät ryhmitellyn valikon.
  const vainKenttatekija = kayttaja.roolit.includes("kenttatekija")
    && !kayttaja.roolit.some((r) =>
         ["paakayttaja", "suunnittelija", "tyonjohtaja", "lukija"].includes(r));

  /*
   * T345: valikkokohta kantaa SYYN, ei pelkkää totuusarvoa.
   *
   * Totuusarvo kertoi ettei kohta näy, muttei miksi — ja siksi
   * piilotettuun osoitteeseen oli vain yksi vastaus: ohjaa muualle
   * hiljaa. Käyttäjä ei nähnyt estettä vaan VÄÄRÄN SIVUN, eikä
   * pääkäyttäjä voinut lukea ruudulta mitä pitäisi myöntää.
   *
   * Syy syntyy samassa lausekkeessa kuin näkyvyys, koska kaksi listaa
   * erkanee: unohtunut lukija ei kaadu vaan on eri mieltä. Näkyvyys on
   * nyt johdettu (`esto === null`) eikä tallennettu.
   */
  const ehto = (...esteet: (Esto | null)[]): Esto | null =>
    esteet.find((e) => e !== null) ?? null;
  const moduuli = (mt: ModuuliTunnus): Esto | null => paalla(mt) ? null
    : { laji: "moduuli", teksti: t("main.esto_moduuli", { moduuli: mt }) };
  const rooli = (sallitut: string[]): Esto | null =>
    kayttaja.roolit.some((r) => sallitut.includes(r)) ? null
      : { laji: "rooli", teksti: t("main.esto_rooli", { sallitut: sallitut.map(roolinNimi).join(", "),
          roolit: kayttaja.roolit.map(roolinNimi).join(", ") || t("main.ei_yhtaan_roolia") }) };
  // Rajattu tunnus näkee omat työmaansa; koko yrityksen näkymät vastaavat
  // API:ssa 403 (`vaadiRajaamaton`), joten valikko sanoo saman syyn.
  const rajaamaton = (): Esto | null => kayttaja.kohteet.length === 0 ? null
    : { laji: "rajaus", teksti: t("main.esto_rajaus") };
  const tyolaji = (lippu: "hitsaustyo" | "laiteasennustyo" | "iv_asennustyo"): Esto | null =>
    kayttaja[lippu] ? null
      : { laji: "tyolaji", teksti: lippu === "hitsaustyo"
          ? t("main.esto_tyolaji_hitsaus")
          : lippu === "iv_asennustyo"
          ? t("main.esto_tyolaji_iv")
          : t("main.esto_tyolaji_laite") };
  const KAUPALLINEN = ["paakayttaja", "suunnittelija"];
  /* H1 (Toni 19.9.2026): tilaajan tunnus ei näe urakoitsijan liiketoimintaa —
     tunnit ovat työkustannuksen peruste, ja henkilöstö, pätevyydet, resurssit
     ja varasto ovat talon omaa tietoa. Sama ehto on API:ssa (`onAsiakastunnus`,
     403 `ei_tilaajalle`); tämä on sen näkymäpuoli, jotta rivi ei lupaa sivua
     joka vastaa 403:lla (T345:n peilikuva). Rajaus on LAJISSA eikä roolissa:
     `lukija` on talon oma rooli, ja asiakas vain sattui saamaan sen. */
  const onAsiakas = kayttaja.laji === "ulkopuolinen" && !kayttaja.roolit.includes("tarkastaja");
  /** T488: tilaajan valvoja = asiakastunnus, jolla on oikeus punakyna_hyvaksynta (koko yritys tai kohde). Sama ehto kuin API:ssa. */
  const valvoja = onAsiakas && (kayttaja.oikeudet.includes("punakyna_hyvaksynta") || (kayttaja.kohdeOikeudet?.["punakyna_hyvaksynta"] ?? []).length > 0);
  /* 217 (Toni 10.10.2026 «Tee kaikki 4»): Academy henkilökohtaisena oikeutena. Sama ehto kuin api/src/auth.ts saaAcademyn —
     roolit pääkäyttäjä, suunnittelija ja oppija aina, muut oikeudella `academy`. */
  const academy = (): Esto | null =>
    ["paakayttaja", "suunnittelija", "oppija"].some((r) => kayttaja.roolit.includes(r)) || kayttaja.oikeudet.includes("academy")
      ? null : { laji: "oikeus", teksti: t("main.esto_academy") };
  const eiTilaajalle = (): Esto | null => kayttaja.laji !== "ulkopuolinen" ? null
    : { laji: "rooli", teksti: t("main.esto_tilaaja") };
  /* 259 (Toni 22.10.2026 klo 10.50): «TG on tunnukselle annettava ominaisuus» — portit näkyvät vain oikeudella tg_nakyma.
     Sama ehto kuin API:ssa (/api/suunnittelu → 403 ilman oikeutta). */
  const tgNakyma = (): Esto | null => kayttaja.oikeudet.includes("tg_nakyma") ? null
    : { laji: "oikeus", teksti: t("main.esto_tg_nakyma") };
  /* 246 (Toni 16.10.2026 klo 04.05): pätevyyksien luku — sama ehto kuin api/src/auth.ts saaPatevyydet. */
  const patevyydetEsto = (): Esto | null =>
    ["paakayttaja", "suunnittelija", "tyonjohtaja"].some((r) => kayttaja.roolit.includes(r))
    || kayttaja.oikeudet.includes("hitsauskoordinointi") || kayttaja.oikeudet.includes("patevyydet_luku")
      ? null : { laji: "oikeus", teksti: t("main.esto_patevyydet") };

  /* T344: tarkastuslaitoksen tunnus. Sama muoto kuin kenttätekijällä —
     valikko on roolin valinta, ja API:n sallittujen lista (TARKASTAJAN_REITIT)
     on sen pari. Kaksi näkymää: linjat tarkastuksineen, ja omat tiedot. */
  const vainTarkastaja = kayttaja.roolit.includes("tarkastaja")
    && !kayttaja.roolit.some((r) =>
         ["paakayttaja", "suunnittelija", "tyonjohtaja", "lukija", "kenttatekija"].includes(r));

  /* T440: käännösten tarkistus — oikeus, ei rooli (tarkistaja voi olla kenttätili). Sama ehto kuin API:n kaannosKieli. */
  const kaannosEsto: Esto | null = kayttaja.oikeudet.includes("kaannostarkistus") || kayttaja.oikeudet.includes("kaannosten_hyvaksynta")
    || kayttaja.roolit.includes("paakayttaja") ? null
    : { laji: "rooli", teksti: t("main.esto_kaannokset") };
  /* TUKI (Tonin havainto 11.10.2026 «testaus ja ohjeet taitaa kuulua Hallinnan (tai sitten omaan lokeroon)», pääChatin
     suunnitelma): sivupalkin alimmainen ryhmä jokaiselle roolille, aina auki. Ohjeet kaikkialla; demossa (TESTAUS=1)
     ensimmäisenä Testaus ja pääkäyttäjälle Testauspalaute (T487). Ei Hallintaan: sen näkee vain pääkäyttäjä. */
  const tukiRyhma = { otsikko: "Tuki", ainaAuki: true, kohdat: [
    ...(kayttaja.testaus ? [
      { avain: "testaus" as const, nimi: "Testaus", esto: null },
      { avain: "testauspalaute" as const, nimi: "Testauspalaute", esto: rooli(["paakayttaja"]) },
    ] : []),
    { avain: "ohjeet" as const, nimi: "Ohjeet", esto: null },
  ] };
  const ryhmat0: { otsikko: string; ainaAuki?: boolean; kohdat: { avain: Nakyma; nimi: string; esto: Esto | null }[] }[] = vainTarkastaja
    ? [{ otsikko: "Tarkastus", kohdat: [
        // Selailussa on linja, sen hitsit, NDT-tarkastukset pöytäkirjoineen,
        // linjatarkastus ja punchit — eli koko se työ jota varten rooli on.
        { avain: "selailu", nimi: "Linjat ja tarkastukset", esto: moduuli("hitsausloki") },
        // T488 (Toni 11.10.2026 «linjatarkastus Tarkastajalle»): nippu omista kohteista — API: TARKASTAJAN_REITIT + NIPUN_KAYTTAJA.
        { avain: "tarkastusnippu", nimi: "Linjatarkastus", esto: moduuli("hitsausloki") },
        { avain: "viestit", nimi: "Viestit",                esto: null },
        { avain: "minun",   nimi: "Omat tiedot",            esto: null },
      ] }, tukiRyhma]
    : vainKenttatekija
    // Otsikko ei ole «Hitsausloki»: sähköasentajan Tuntikirjaus ja Laiteasennus eivät ole hitsausta (13.9.2026).
    ? [{ otsikko: "Kenttätyö", kohdat: [
        // Hitsausloki vain hitsaustyön työlajilla (127); sama ehto kuin API:n portissa.
        { avain: "hitsausloki",  nimi: "Hitsausloki",  esto: ehto(moduuli("hitsausloki"), tyolaji("hitsaustyo")) },
        /* Selailu kenttätekijälle (Toni 21.9.2026): omat kohteet, ei
           muokkausta, punakynän esitykset sallittuja. Rajaus on
           palvelimella (`kohdeRajaus` + roolipohjainen `naekeeKaiken`),
           ei tässä — valikko on navigointia eikä porttia. Sama työlajiehto
           kuin Hitsauslokilla: sähköasentaja ei näe hitsauksen tietoja. */
        { avain: "selailu",      nimi: "Selailu",      esto: ehto(moduuli("hitsausloki"), tyolaji("hitsaustyo")) },
        // 213 (T445): kenttätilille vain oikeudella hitsausloki_tyonjohto (207) — sama ehto kuin API:n kuittausOikeus.
        // Toni 15.10.2026 klo 18.30: Puuttuvat hitsit vain master-adapterin instanssissa (MASTER_ADAPTERI, pekilo).
        ...(kayttaja.master_adapteri ? [{ avain: "kuittaus" as const,     nimi: "Puuttuvat hitsit", esto: ehto(moduuli("hitsausloki"),
            kayttaja.oikeudet.includes("hitsausloki_tyonjohto") ? null
              : { laji: "rooli" as const, teksti: t("main.esto_kuittaus") }) }] : []),
        // Kentän kirjaajalla on paras havainto siitä mikä on pielessä.
        // Kirjausoikeus on tarkoituksellinen (017 §ratkaisija); luku on
        // rajattu omiin kohteisiin ja ulkoisen vastuun nimi jää pois.
        { avain: "puutteet",     nimi: "Puutteet",     esto: moduuli("puutteet") },
        { avain: "tuntikirjaus", nimi: "Tuntikirjaus", esto: eiTilaajalle() },
        // Asentaja kuittaa liitokset puhelimesta (085: yksi kutsu per
        // painallus) — näkymä on rakennettu kenttää varten, mutta se
        // puuttui kenttätilin valikosta 12.9.2026 asti (Pro-Tot 20.9., A3).
        // Reitit /api/laitteet ja /api/liitokset eivät ole hitsaajan kiellossa.
        // 134: kenttätekijälle laiteasennus näkyy vain laiteasennustyön työlajilla — sama
        // portti kuin hitsauslokilla, ja valikko käyttää samaa ehtoa kuin API.
        { avain: "laiteasennus", nimi: "Laiteasennus",
          esto: ehto(moduuli("laiteasennus"), tyolaji("laiteasennustyo")) },
        // 191 (päätös 52): IV-asennus kenttätekijälle vain IV-asennustyön työlajilla — sama ehto kuin API:n portti.
        { avain: "iv_asennus", nimi: "IV-asennus",
          esto: ehto(moduuli("iv_asennus"), tyolaji("iv_asennustyo")) },
        // Kenttätunnus riittää opiskeluun: sama kirjautuminen, sama
        // puhelin. Ilman tätä Academy olisi olemassa ja
        // saavuttamaton juuri niille joita varten se tehtiin.
        { avain: "academy",      nimi: "Academy",      esto: ehto(moduuli("osaaminen"), academy()) },
        /* Korjaus a (Toni 28.9.2026): naarmuuntunut QR, luettava numero. Vie samaan näkymään kuin
           skannaus (#/k → numero → #/k/<n>); ennen tätä ainoa tie oli kirjoittaa osoite käsin. */
        { avain: "k",            nimi: "Kalusto",       esto: moduuli("kalusto") },   // 53 (Toni 2.10.2026): «Tarran numero» → «Kalusto»
        // 162: tiedotteet koskevat myös kenttätiliä (kaikille / yksittäinen).
        { avain: "viestit",      nimi: "Viestit",      esto: null },
        { avain: "minun",        nimi: "Omat tiedot",  esto: null },
        // Toni 10.10.2026: käännöstyö vain demossa; asiakasinstanssissa kohtaa ei ole (API 404).
        ...(kayttaja.kaannostyo ? [{ avain: "kaannokset" as const, nimi: "Käännökset", esto: kaannosEsto }] : []),
      ] }, tukiRyhma]
    : [
    { otsikko: "Projekti", kohdat: [
      // Testaus ja Ohjeet ovat TUKI-ryhmässä (11.10.2026); Projektiin jäävät Hub, Puutteet ja Luovutusvalmius.
      { avain: "hub", nimi: "Projekti-Hub", esto: null },
      // T373 (Toni 13.10.2026): kolme viikkoa tästä viikosta — tilaaja saa oman version ilman nimiä myöhemmin.
      { avain: "suunnitelma", nimi: "3-viikkoissuunnitelma", esto: eiTilaajalle() },
      // 254 (T625 vaihe 1, Toni 21.10.2026): kohteen portit — Suunnittelu-moduuli, ei tilaajalle (API:n sallittu-lista ei avaa sitä).
      { avain: "portit", nimi: "Portit (TG)", esto: ehto(moduuli("suunnittelu"), eiTilaajalle(), tgNakyma()) },
      // Puutteet on eri moduuli kuin Luovutusvalmius: jälkimmäinen on
      // lähdeaukot ja dokumentointivelka, jotka ovat putkidomainia.
      // 10.10.2026 Toni «tilaajamuutokset edellä mainituin tavoin»: Puutteet pois tilaajalta (valikko ja reitit).
      { avain: "puutteet", nimi: "Puutteet", esto: ehto(moduuli("puutteet"), eiTilaajalle()) },
      { avain: "aukot", nimi: "Luovutusvalmius", esto: moduuli("hitsausloki") },
      // T488: vain tilaajan valvojalle (oikeus punakyna_hyvaksynta) — muut näkevät jonon Selailussa.
      ...(valvoja ? [{ avain: "vahvistettavat" as const, nimi: "Odottaa vahvistusta", esto: moduuli("hitsausloki") }] : []),
    ] },
    { otsikko: "Myynti", kohdat: [
      // 13.9.2026: kaupallinen tieto — sama portti kuin API:ssa (pääkäyttäjä tai suunnittelija, ei rajattu).
      { avain: "tarjous",  nimi: "Tarjoukset", esto: ehto(moduuli("tarjous"), rooli(KAUPALLINEN), rajaamaton()) },
      { avain: "materiaalit", nimi: "Materiaalit", esto: ehto(moduuli("tarjous"), rooli(KAUPALLINEN), rajaamaton()) },
      { avain: "asiakkaat", nimi: "Asiakkaat",
        esto: ehto(moduuli("tarjous"), rooli(KAUPALLINEN)) },
      // 13.9.2026: koko yrityksen vapaa kapasiteetti — ei rajatulle (API 403).
      { avain: "mitoitus", nimi: "Mitoitus",   esto: ehto(moduuli("mitoitus"), rajaamaton()) },
    ] },
    { otsikko: "Resursointi", kohdat: [
      { avain: "resurssit", nimi: "Resurssit",  esto: eiTilaajalle() },
      { avain: "kohteet",   nimi: "Työkohteet", esto: null },
      // 13.9.2026: koko yrityksen rotaatioryhmät nimineen — ei rajatulle (API 403).
      { avain: "rotaatio",  nimi: "Työvuorot",  esto: rajaamaton() },
    ] },
    { otsikko: "Väki & kalusto", kohdat: [
      // A2 (erä 22, 9.9.2026): näyttönimi sisällön mukaan — henkilöstö, ei «henkilöt».
      { avain: "henkilot",   nimi: "Henkilöstö", esto: eiTilaajalle() },
      { avain: "patevyydet", nimi: "Pätevyydet", esto: ehto(moduuli("patevyydet"), eiTilaajalle(), patevyydetEsto()) },
      // Pätevyys on ulkopuolisen myöntämä ja vanhenee kalenterissa;
      // osaamistaso johdetaan tehdystä työstä. Vierekkäin, ei sisäkkäin.
      // Moduuliportti EI riita: nakyman ydinsisalto (osaamiskortit)
      // vaatii roolin, ja ilman sita nakyma naytti pelkan
      // "Sinulla ei ole oikeutta" -bannerin. Navi joka tarjoaa kohtaa
      // jota rooli ei voi kayttaa on sama vika kuin piilotettu nakymä
      // ilman porttia, toisin pain: §Portti erehtyy kiinni pain.
      { avain: "osaaminen",  nimi: "Osaamisradat",
        esto: ehto(moduuli("osaaminen"),
                   rooli(["paakayttaja", "suunnittelija", "tyonjohtaja"])) },
      // 10.10.2026 (Toni): Academy ei tilaajalle (valikko ja reitit, sallitut.ts).
      { avain: "academy",    nimi: "Academy",      esto: ehto(moduuli("osaaminen"), eiTilaajalle(), academy()) },
      // 13.9.2026: yrityksen omaisuus — ei rajatulle (API 403).
      { avain: "kalusto",    nimi: "Kalusto",    esto: ehto(moduuli("kalusto"), rajaamaton()) },
      { avain: "minun",      nimi: "Omat tiedot", esto: null },
      // Toni 15.10.2026 (valikkolohko kohta 4): Tuntikirjaus Omat tiedot -kohdan viereen; oikeus ennallaan.
      { avain: "tuntikirjaus", nimi: "Tuntikirjaus", esto: eiTilaajalle() },
      { avain: "viestit",    nimi: "Viestit",     esto: null },
    ] },
    { otsikko: "Tuotanto", kohdat: [
      /* Valikossa ei näy näkymää jota ei ole annettu (Toni 26.9.2026). Kävely mittasi että tilaaja
         näki rivit Hitsausloki ja Ainestodistukset ja sai näkymän sisällä «Ei oikeutta». Roolilista
         on sama kuin näkymän oma vartija ja API: `saaKirjata` / POST /api/hitsit ja `saaKasitella` /
         AINE_ROOLIT — kolme paikkaa, yksi sääntö. */
      { avain: "hitsausloki", nimi: "Hitsausloki", esto: ehto(moduuli("hitsausloki"),
          rooli(["paakayttaja", "suunnittelija", "tyonjohtaja", "kenttatekija"])) },
      { avain: "selailu",     nimi: "Selailu",     esto: moduuli("hitsausloki") },
      // 213 (T445): sama roolijoukko kuin API:n kuittausOikeus.
      ...(kayttaja.master_adapteri ? [{ avain: "kuittaus" as const,    nimi: "Puuttuvat hitsit", esto: ehto(moduuli("hitsausloki"),
          rooli(["paakayttaja", "suunnittelija", "tyonjohtaja"])) }] : []),
      // Päätös 90: nippu — sama roolijoukko kuin API (NIPUN_KAYTTAJA) ja linjatarkastuksen tallennus.
      { avain: "tarkastusnippu", nimi: "Linjatarkastus", esto: ehto(moduuli("hitsausloki"),
          rooli(["paakayttaja", "suunnittelija", "tyonjohtaja", "tarkastaja"])) },
      // Tilaaja näkee työn, ei työvoimaa (Toni 26.9.2026): sama ehto kuin API:n ASIAKKAALTA_KIELLETYT.
      { avain: "tyopaketit",  nimi: "Työpaketit",  esto: ehto(moduuli("hitsausloki"), eiTilaajalle()) },
      { avain: "varasto",     nimi: "Varasto",     esto: ehto(moduuli("hitsausloki"), eiTilaajalle()) },
      { avain: "laiteasennus", nimi: "Laiteasennus", esto: moduuli("laiteasennus") },
      // 189 (2.10.2026): IV-asennus — järjestelmä → kanavaosuus → pääte, mittaukset mittalaitteineen.
      { avain: "iv_asennus", nimi: "IV-asennus", esto: moduuli("iv_asennus") },
    ] },
    /* Toni 15.10.2026 (valikkolohko): SEURANTA JA RAPORTOINTI (Etenemä; nimi Toni 15.10. «RAPORTIT → SEURANTA JA RAPORTOINTI») ja LUOVUTUSDOKUMENTAATIO (Ainestodistukset nyt; myöhemmin luovutuskansio
       T443 ja laippaliitokset; korvaa 7.10. LAATU-ryhmän ajatuksen). Oikeudet ennallaan, vain sijainti muuttuu. Selailu jää
       Tuotantoon (Toni 15.10.2026 «B: Tuotanto-ryhmään»). */
    { otsikko: "Seuranta ja raportointi", kohdat: [
      { avain: "etenema",     nimi: "Etenemä",     esto: moduuli("hitsausloki") },
      // T646 (1) ja T514 (Toni 21.10.2026 klo 10.12, 23.10.2026 klo 05.35): tilaajalle kiinni kunnes erikseen päätetään.
      { avain: "linjatarkastukset",   nimi: "Linjatarkastukset",          esto: moduuli("hitsausloki") },
      { avain: "koeponnistusvalmius", nimi: "Valmiina koeponnistukseen",  esto: moduuli("hitsausloki") },
    ] },
    { otsikko: "Luovutusdokumentaatio", kohdat: [
      // 178 (Toni 26.9.2026): lukuoikeus rinnalle — koko yritys tai kohde, sama ehto kuin API ja näkymä.
      // 10.10.2026 (Toni): tilaaja lukee oman kohteensa todistukset — sama ehto kuin API:n ainestodistustenKohteet.
      { avain: "ainestodistukset", nimi: "Ainestodistukset", esto: ehto(moduuli("hitsausloki"),
          (kayttaja.oikeudet.includes("ainestodistukset_luku") || onAsiakas
            || (kayttaja.kohdeOikeudet?.["ainestodistukset_luku"] ?? []).length > 0)
            ? null : rooli(["paakayttaja", "suunnittelija", "tyonjohtaja"])) },
    ] },
    { otsikko: "Hallinta", kohdat: [
      // A2 (erä 22, 9.9.2026): näkymä sisältää tunnukset, kutsut ja kenttätilit — «Tunnukset».
      { avain: "kayttajat", nimi: "Tunnukset", esto: rooli(["paakayttaja"]) },
      ...(kayttaja.kaannostyo ? [{ avain: "kaannokset" as const, nimi: "Käännökset", esto: kaannosEsto }] : []),
      { avain: "tarjousasetukset", nimi: "Tarjousasetukset",
        esto: ehto(moduuli("tarjous"), rooli(["paakayttaja"])) },
      { avain: "yritys", nimi: "Yrityksen tiedot",
        esto: rooli(["paakayttaja"]) },
    ] },
    tukiRyhma,
  ];

  /* 10.10.2026 Toni («asiakkaalla näkyy KAUHEESTI kaikkee tuotannollista»): tilaajan valikko on SALLITTUJEN lista, ei
     kieltolista — uusi näkymä ei ilmesty tilaajalle ohimennen. Sama joukko kuin API:n TILAAJAN_SALLITUT (sallitut.ts). */
  // «testaus» vain kun TESTAUS=1 (demo): tilaaja on yksi testattavista rooleista (Toni 10.10.2026). Muualla täsmälleen viisi.
  const TILAAJAN_NAKYMAT: readonly Nakyma[] = ["hub", "aukot", "etenema", "ainestodistukset", "minun", "ohjeet",
    ...(valvoja ? ["vahvistettavat" as const] : []), ...(kayttaja.testaus ? ["testaus" as const] : [])];
  asetaAvattavat(onAsiakas ? TILAAJAN_NAKYMAT : null);
  const ryhmat = onAsiakas
    ? ryhmat0.map((r) => ({ ...r, kohdat: r.kohdat.map((k) => TILAAJAN_NAKYMAT.includes(k.avain) ? k : { ...k, esto: k.esto ?? eiTilaajalle() }) }))
    : ryhmat0;

  /*
   * VARTIJA: kelvollinen tunnus nakymaan jota kayttaja ei nae.
   *
   * Hash on nyt osoitteessa, joten hitsaaja voi avata `#/tarjous`
   * vaikka se ei ole hanen navissaan. Ennen sita ei voinut: nakyman
   * saattoi vaihtaa vain klikkaamalla nakyvaa linkkia. Ilman tata
   * renderointiehdot (`nakyma === "tarjous" && paalla(...)`) eivat
   * osu mihinkaan ja sisaltoalue jaa TYHJAKSI -- eli juuri se oire
   * jota vastaan koko paivan portit on rakennettu.
   *
   * Paluu ei ole OLETUSNAKYMA vaan ensimmainen NAKYVA kohta:
   * hitsaajan navissa ei ole "resurssit"-nakymaa lainkaan, joten
   * oletukseen palaaminen olisi sekin tyhja ruutu.
   */
  const nakyvatKohdat = ryhmat.flatMap((r) => r.kohdat).filter((k) => !k.esto);
  const ensimmainen: Nakyma = nakyvatKohdat[0]?.avain ?? OLETUSNAKYMA;
  // Vertailu on `nakyma`, EI `nayta`: `nayta` on juuri tassa
  // maarittelyssa eika ole viela olemassa. Kattava korvaus
  // `nakyma ===` -> `nayta ===` osui 31.8.2026 tahan omaan riviinsa,
  // ja tulos oli itseensa viittaava const.
  //
  // ⚠ tsc EI kaatunut siihen: viittaus on nuolifunktion sisalla,
  // joten sulkeuma VOISI ajaa vasta myohemmin. Ajonaikana se on
  // temporal dead zone -- "Cannot access 'f' before initialization"
  // -- ja se nakyi vasta sisaankirjautuneena. Vientiportti antoi
  // vihrean, koska kirjautumissivu piirtyy normaalisti.
  const nayta: Nakyma = nakyvatKohdat.some((k) => k.avain === nakyma)
    ? nakyma : ensimmainen;

  /*
   * T345: piilotettu osoite saa nimetyn vastauksen, ei toista näkymää.
   *
   * `nayta` jää ennalleen, koska sivupalkki ja sisältö tarvitsevat yhä
   * kelvollisen avaimen — mutta kun `esto` on olemassa, sisältöä ei
   * renderöidä lainkaan eikä yksikään valikkorivi ole aktiivinen.
   *
   * Avain jota tämän tunnuksen valikossa EI OLE LAINKAAN (kenttätilin
   * valikossa on kuusi kohtaa 27:stä) ei voi kantaa omaa syytään, koska
   * riviä ei ole. Sen syy on rooli: valikon rakenne on roolin valinta,
   * ja se sanotaan ääneen samoin sanoin kuin näkyvän kohdan rooliesto.
   */
  const pyydettyKohta = ryhmat.flatMap((r) => r.kohdat)
    .find((k) => k.avain === nakyma);
  const esto: Esto | null = pyydettyKohta
    ? pyydettyKohta.esto
    : { laji: "rooli", teksti: t("main.esto_ei_valikossa", { nakyma,
        roolit: kayttaja.roolit.map(roolinNimi).join(", ") || t("main.ei_yhtaan") }) };

  // `nayta`, EI `nakyma`: otsikko kertoo mita RUUDULLA on, ei mita
  // osoitteessa pyydettiin. Tassa luki `nakyma` 31.8.2026 asti, ja
  // silloin lukijalle avattu `#/kayttajat` naytti otsikon
  // "Kayttajat" vaikka sisalto oli oikein vaihtunut ensimmaiseen
  // nakyvaan. Otsikko ja sisalto eri mielta -- sama vika kuin
  // punaisella solulla jonka selitys puhui viikkorajasta.
  const nakymanNimi = esto ? t("esto.otsikko")
    : valikonTeksti(ryhmat.flatMap((r) => r.kohdat).find((k) => k.avain === nayta)?.nimi);

  return (
    <KohdeKontekstiTarjoaja>
    <div className="app" key={kieliAvain} data-kieli-avain={kieliAvain}>
      <Otsikko nimi={nakymanNimi} brandi={brandi} />
      <div className="palkki">
        <Tunnus brandi={brandi} />
        <button className="palkki__valikko" aria-label={t("main.valikko")}
                aria-expanded={valikkoAuki}
                onClick={() => setValikkoAuki(!valikkoAuki)}>
          <span /><span /><span />
        </button>

        <div className="palkki__oikea">
          {/* T426: yksi kohdevalinta koko sovellukselle. Puhelimessa valikon yläreunassa (alla), ks. tyylit.css. */}
          <div className="palkki__kohde-selain"><YlapalkinKohdevalinta /></div>
          {/* Puhelimessa nimen paikalla kohteen tunnus — aina näkyvissä (pääChatin huomio 7.10.2026). */}
          <div className="palkki__kohde-puhelin"><YlapalkinKohdevalinta lyhyt /></div>
          {/* Kielivalikko «FI ▾» (Tonin ehdotus 11.10.2026): kaikissa näkymissä ja rooleilla; tallentuu tunnukselle.
              Puhelimessa (≤ 640 px) se on valikon yläreunassa, ks. sivupalkki__kieli: yläpalkissa se rivitti palkin
              (oikea lohko 130 → 194 px, palkki 134 px) ja demon kävely 67c9c42 mittasi Ainestodistukset 815 ≥ 812. */}
          <span className="palkki__kieli-selain"><YlapalkinKieli /></span>
          {/* 11.10.2026 (Toni, TUKI): «?» vie Ohjeisiin mistä tahansa näkymästä vierittämättä; kaikissa instansseissa. */}
          <button type="button" className="palkki__ohje" data-ohje-linkki="" aria-label={t("yla.ohjeet")} title={t("yla.ohjeet")}
                  onClick={() => { setNakyma("ohjeet"); setOsoiteKohde(null); setOsoiteHaku(""); setValikkoAuki(false); }}>?</button>
          <button className="palkki__kayttaja" onClick={() => setSalasana(true)}
                  title={t("main.vaihda_salasana")}>
            {kayttaja.nimi}
          </button>
          <button
            className="nappi nappi--kevyt palkki__ulos"
            onClick={ulos}
          >
            {t("yla.kirjaudu_ulos")}
          </button>
        </div>
      </div>

      {salasana && <Salasana sulje={() => setSalasana(false)} />}

      <div className="runko">
        <aside className={"sivupalkki" + (valikkoAuki ? " sivupalkki--auki" : "")}>
          {/* T426 puhelimessa: yläpalkin valitsin rivitti palkin (demon kävely 54e717a: Hubin taite 375 px 836 ≥ 812). */}
          <div className="sivupalkki__kohde">
            <YlapalkinKohdevalinta />
            <div className="sivupalkki__kieli"><YlapalkinKieli /></div>
            {/* Nimi, salasanan vaihto ja uloskirjautuminen puhelimessa täällä: yläpalkissa on niiden paikalla kohde.
                Demon kävely e589b43 ja 68d95e4: kohteen tunnus «Kirjaudu ulos» -napin vieressä rivitti palkin, ja jokainen
                näkymä siirtyi 45 px alemmas (tuntikirjaus 1062 → 1107, osaaminen 820 → 865, hub ja selailu yli 812). */}
            <button className="tekstilinkki" onClick={() => setSalasana(true)} style={{ marginTop: 6 }}>{kayttaja.nimi} · {t("yla.vaihda_salasana")}</button>
            <button className="tekstilinkki" data-ulos-valikosta="" style={{ marginTop: 6, display: "block" }}
                    onClick={ulos}>{t("yla.kirjaudu_ulos")}</button>
          </div>
          {ryhmat.map((r, _i, kaikki) => {
            const nakyvat = r.kohdat.filter((k) => !k.esto);
            if (nakyvat.length === 0) return null;
            // Yksi ryhmä (kenttätili: «Kenttätyö») ei taitu: ainoan valikon piilottaminen ei säästä mitään.
            const ainoa = kaikki.filter((x) => !x.ainaAuki && x.kohdat.some((k) => !k.esto)).length === 1 && !r.ainaAuki;
            // T539: auki = nykyisen sivun ryhmä (ellei itse suljettu) + viimeksi avattu. Enintään kaksi.
            const tunnus = ryhmanTunnus(r.otsikko);
            const oma = nakyvat.some((k) => k.avain === nayta);
            const auki = ainoa || !!r.ainaAuki || oma || avattuRyhma === tunnus;
            return (
              <div key={r.otsikko} className={"sivupalkki__ryhma" + (auki ? "" : " sivupalkki__ryhma--kiinni")}
                   data-valikkoryhma={tunnus} data-auki={auki ? "1" : "0"}>
                <h3 className="sivupalkki__otsikko">
                  {ainoa || r.ainaAuki ? valikonTeksti(r.otsikko) : (
                    <button type="button" className="sivupalkki__ryhmanappi" aria-expanded={auki}
                            onClick={() => {
                              // Oma ryhmä ei sulkeudu (aina auki); muu ryhmä avautuu tai sulkeutuu, ja valinta tallentuu.
                              if (!oma) setAvattuRyhma(auki ? null : tunnus);
                            }}>
                      <span>{valikonTeksti(r.otsikko)}</span><span aria-hidden="true">{auki ? "⌃" : "⌄"}</span>
                    </button>
                  )}
                </h3>
                {/* T539: suljetun ryhmän kohdat ovat DOMissa piilotettuina (hidden): kävelyt ja hakutyökalut lukevat koko valikon
                    avaamatta ryhmiä, joita on enintään kaksi auki kerrallaan. */}
                {nakyvat.map((k) => (
                  <button key={k.avain} data-nakyma={k.avain} hidden={!auki}
                    className={"sivupalkki__linkki" + (!esto && nayta === k.avain ? " sivupalkki__aktiivinen" : "")}
                    onClick={() => { setNakyma(k.avain); setOsoiteKohde(null); setOsoiteHaku("");
                                     setValikkoAuki(false);
                                     // T539: siirtyminen avatun ryhmän sivulle tekee siitä nykyisen; edellinen nykyinen sulkeutuu.
                                     if (avattuRyhma === tunnus) setAvattuRyhma(null); }}
                  >{valikonTeksti(k.nimi)}
                    {/* 162: punainen piste — lukemattomien määrä, ei pelkkä väri. */}
                    {k.avain === "viestit" && lukematta > 0 && (
                      <span className="piste" data-lukematta={lukematta}
                            aria-label={t("main.lukematonta", { n: lukematta })}>{lukematta}</span>
                    )}
                    {/* T534 jatko (Toni 13.10.2026): omien tehtävien määrä Omat tiedot -kohdassa, sama mekanismi kuin viesteillä. */}
                    {k.avain === "minun" && tehtavia > 0 && (
                      <span className="piste" data-tehtavia={tehtavia}
                            aria-label={t("main.tehtavia", { n: tehtavia })}>{tehtavia}</span>
                    )}</button>
                ))}
              </div>
            );
          })}
        </aside>

        <main className="sisalto">
          {esto && <EiOikeutta esto={esto} osoite={nakyma} nimi={pyydettyKohta?.nimi}
                     siirryNimi={nakyvatKohdat.find((k) => k.avain === ensimmainen)?.nimi}
                     siirto={() => { setNakyma(ensimmainen); setOsoiteKohde(null);
                                     setOsoiteHaku(""); }} />}
          {!esto && <>
          {nayta === "tarjous"    && paalla("tarjous")     && <Tarjoukset kayttaja={kayttaja} />}
          {nayta === "materiaalit" && paalla("tarjous")    && <Materiaalit kayttaja={kayttaja} osoiteHaku={osoiteHaku} asetaOsoiteHaku={setOsoiteHaku} />}
          {nayta === "asiakkaat"   && paalla("tarjous")    && <Asiakkaat kayttaja={kayttaja} osoiteHaku={osoiteHaku} asetaOsoiteHaku={setOsoiteHaku} />}
          {nayta === "yritys"      && kayttaja.roolit.includes("paakayttaja")
          && <Yrityksentiedot kayttaja={kayttaja} sertifikaatit={paalla("tarjous")} />}
          {nayta === "resurssit" && <Kapasiteetti kayttaja={kayttaja} />}
          {nayta === "kohteet"   && <Kohteet kayttaja={kayttaja} osoiteHaku={osoiteHaku} asetaOsoiteHaku={setOsoiteHaku} />}
          {nayta === "mitoitus"   && paalla("mitoitus")   && <Mitoitus />}
          {nayta === "henkilot"  && <Henkilot kayttaja={kayttaja} osoiteHaku={osoiteHaku} asetaOsoiteHaku={setOsoiteHaku} />}
          {nayta === "rotaatio"  && <Rotaatio kayttaja={kayttaja} />}
          {nayta === "hub"       && <Hub kayttaja={kayttaja} osoiteKohde={osoiteKohde}
                                         asetaOsoiteKohde={setOsoiteKohde} />}
          {nayta === "puutteet"  && paalla("puutteet") && <PuutteetNakyma kayttaja={kayttaja}
                                         osoiteHaku={osoiteHaku} asetaOsoiteHaku={setOsoiteHaku} />}
          {nayta === "suunnitelma" && <Suunnitelma kayttaja={kayttaja} />}
          {nayta === "aukot"     && paalla("hitsausloki") && <LaatuAukot kayttaja={kayttaja}
                                         osoiteHaku={osoiteHaku} asetaOsoiteHaku={setOsoiteHaku} />}
          {nayta === "ainestodistukset" && paalla("hitsausloki") && <Ainestodistukset kayttaja={kayttaja} />}
          {nayta === "hitsausloki" && paalla("hitsausloki") && <Hitsausloki kayttaja={kayttaja} />}
          {nayta === "kuittaus"    && paalla("hitsausloki") && kayttaja.master_adapteri && <Hitsikuittaus kayttaja={kayttaja} />}
          {nayta === "selailu"     && paalla("hitsausloki")
            && <Selailu kayttaja={kayttaja} osoiteKohde={osoiteKohde}
                        asetaOsoiteKohde={setOsoiteKohde}
                        osoiteHaku={osoiteHaku} asetaOsoiteHaku={setOsoiteHaku} />}
          {nayta === "vahvistettavat" && paalla("hitsausloki") && <Selailu kayttaja={kayttaja} vainVahvistettavat />}
          {nayta === "tarkastusnippu" && paalla("hitsausloki")
            && <Tarkastusnippu kayttaja={kayttaja} osoiteKohde={osoiteKohde} asetaOsoiteKohde={setOsoiteKohde} />}
          {nayta === "etenema"     && paalla("hitsausloki") && <Etenema />}
          {nayta === "linjatarkastukset"   && paalla("hitsausloki") && <Linjatarkastukset />}
          {nayta === "koeponnistusvalmius" && paalla("hitsausloki") && <Koeponnistusvalmius masterAdapteri={!!kayttaja.master_adapteri} />}
          {nayta === "tyopaketit"  && paalla("hitsausloki") && <Tyopaketit kayttaja={kayttaja} />}
          {nayta === "varasto"     && paalla("hitsausloki") && <Varasto kayttaja={kayttaja} />}
          {nayta === "minun"       && <OmatTiedot kayttaja={kayttaja} hitsauslokiPaalla={paalla("hitsausloki")} />}
          {nayta === "tuntikirjaus" && <Tuntikirjaus kayttaja={kayttaja} />}
          {nayta === "viestit"     && <Viestit kayttaja={kayttaja} luettuMuuttui={haeLukematta} />}
          {nayta === "kaannokset"  && kayttaja.kaannostyo && <KaannosTarkistus kayttaja={kayttaja} />}
          {nayta === "testaus"     && kayttaja.testaus && <Testaus />}
          {nayta === "ohjeet"      && <Ohjeet />}
          {nayta === "testauspalaute" && kayttaja.testaus && kayttaja.roolit.includes("paakayttaja") && <Testauspalaute />}
          {nayta === "patevyydet"  && paalla("patevyydet")  && <Patevyydet kayttaja={kayttaja} osoiteHaku={osoiteHaku} asetaOsoiteHaku={setOsoiteHaku} />}
          {nayta === "kalusto"     && paalla("kalusto")     && <Kalusto kayttaja={kayttaja} osoiteHaku={osoiteHaku} asetaOsoiteHaku={setOsoiteHaku} />}
            {nayta === "laiteasennus" && paalla("laiteasennus") && <Laiteasennus kayttaja={kayttaja} osoiteHaku={osoiteHaku} asetaOsoiteHaku={setOsoiteHaku} />}
          {nayta === "iv_asennus"  && paalla("iv_asennus")  && <IvAsennus kayttaja={kayttaja} />}
          {nayta === "portit"      && paalla("suunnittelu") && <Portit kayttaja={kayttaja} />}
          {nayta === "osaaminen"   && paalla("osaaminen")   && <Osaaminen kayttaja={kayttaja} />}
          {nayta === "academy"     && paalla("osaaminen")   && <Academy kayttaja={kayttaja} />}
          {nayta === "kayttajat"  && <Kayttajat kayttaja={kayttaja} />}
          {nayta === "tarjousasetukset" && paalla("tarjous")
            && kayttaja.roolit.includes("paakayttaja") && <Tarjousasetukset kayttaja={kayttaja} />}
          </>}
        </main>
      </div>
      {/* T487: Palaute-nappi jokaisella demon sivulla (TESTAUS=1); osoite, kieli ja laite tallentuvat automaattisesti. */}
      {/* T577 (Toni 14.10.2026): Palaute-nappi kaikilla sivuilla kaikissa instansseissa, vain selaintunnuksille — kenttätilillä ei
         toistaiseksi. Palaute menee Sampo Prolle (hallinnan Tehtäväni), ei asiakkaan pääkäyttäjälle. */}
      {!kayttaja.kenttatili && <PalauteNappi osoite={"#/" + nayta} />}
    </div>
    </KohdeKontekstiTarjoaja>
  );
}

const juuri = document.getElementById("juuri");
if (!juuri) throw new Error("Elementtiä #juuri ei löydy");

// /kutsu/<token> on julkinen polku: saajalla ei ole salasanaa eikä
// istuntoa, joten kirjautumissivu ei auttaisi häntä mitenkään.
const kutsuOsuma = /^\/kutsu\/([A-Za-z0-9_-]{20,})\/?$/.exec(window.location.pathname);

createRoot(juuri).render(
  <StrictMode>
    <UusiVersio />
    {kutsuOsuma ? <Kutsu token={kutsuOsuma[1]!} /> : <Sovellus />}
  </StrictMode>
);
